import { patientSeed, hasItem } from './planState';
import {
  field, grid, chips, card, careTeamFields, patientInformationChildren, insuranceCard, goalsCard, medicationListCard, monitorCard,
  emergencyCards, educationCards, signoffCards, FREQUENCIES, countDone,
} from './sectionBuilders';
import { normalizePatientChronicConditions } from '../../../../utils/patientChronicConditionUtils';
import { formatIcdOptionLabel } from '../../../../utils/patientMonitoringIcdUtils';

export function findArrhythmiaCondition(patient) {
  if (!patient) return null;
  const raw = patient.chronic_conditions
    ?? patient.chronicConditions
    ?? patient.icd_codes
    ?? patient.patient_icd_codes
    ?? patient.conditions
    ?? [];
  const list = normalizePatientChronicConditions(raw);
  if (!list.length) return null;

  const isArrhythmia = (c) => {
    if (!c) return false;
    const code = String(c.icd_code || '').trim().toUpperCase();
    const name = String(c.condition_name || '').trim().toLowerCase();

    if (/^(I4[45789]|R00)/.test(code)) return true;

    const keywords = [
      'arrhythmia',
      'dysrhythmia',
      'atrial fibrillation',
      'afib',
      'a-fib',
      'atrial flutter',
      'tachycardia',
      'bradycardia',
      'heart block',
      'av block',
      'bundle branch',
      'sick sinus',
      'sinus pause',
      'sinus arrest',
      'ventricular fibrillation',
      'vfib',
      'v-fib',
      'ventricular tachycardia',
      'premature atrial',
      'pac',
      'premature ventricular',
      'pvc',
      'long qt',
      'brugada',
      'wpw',
      'wolff-parkinson',
      'supraventricular',
      'svt',
    ];
    return keywords.some((kw) => name.includes(kw));
  };

  const primaryArrhythmia = list.find((c) => c.billing_role === 'primary' && isArrhythmia(c));
  if (primaryArrhythmia) return primaryArrhythmia;

  const anyArrhythmia = list.find(isArrhythmia);
  if (anyArrhythmia) return anyArrhythmia;

  const singlePrimary = list.find((c) => c.billing_role === 'primary');
  if (singlePrimary && list.length === 1) return singlePrimary;

  if (list.length === 1 && isArrhythmia(list[0])) return list[0];

  return null;
}

const ARRHYTHMIA_GROUPS = [
  { id: 'brady', label: 'Bradyarrhythmias & conduction disease', items: [
    'Sinus bradycardia', 'Sinus node dysfunction / sick sinus', 'Tachy-brady syndrome',
    'Sinus pause or arrest', 'First-degree AV block', 'Second-degree AV block (Mobitz I)',
    'Second-degree AV block (Mobitz II)', 'Complete (third-degree) AV block',
    'Bundle branch block / bifascicular', 'Chronotropic incompetence',
  ] },
  { id: 'svt', label: 'Supraventricular arrhythmias', items: [
    'Atrial fibrillation — paroxysmal', 'Atrial fibrillation — persistent',
    'Atrial fibrillation — long-standing persistent', 'Atrial fibrillation — permanent',
    'Atrial flutter — typical', 'Atrial flutter — atypical', 'AVNRT',
    'AVRT / accessory pathway (WPW)', 'Focal atrial tachycardia', 'Multifocal atrial tachycardia',
    'Inappropriate sinus tachycardia', 'POTS', 'Premature atrial contractions (PACs)',
    'Junctional rhythm / tachycardia',
  ] },
  { id: 'vt', label: 'Ventricular arrhythmias', items: [
    'Premature ventricular contractions (PVCs)', 'Non-sustained VT (NSVT)',
    'Sustained monomorphic VT', 'Polymorphic VT', 'Torsades de pointes',
    'Ventricular fibrillation / arrest survivor', 'Idiopathic VT (RVOT / fascicular)',
    'Electrical storm history', 'Accelerated idioventricular rhythm',
  ] },
  { id: 'inherited', label: 'Inherited syndromes & channelopathies', items: [
    'Long QT syndrome', 'Short QT syndrome', 'Brugada syndrome', 'CPVT', 'ARVC',
    'Hypertrophic cardiomyopathy', 'Early repolarization syndrome',
    'Family history of sudden cardiac death', 'Other',
  ] },
];

const AF_FLUTTER = ARRHYTHMIA_GROUPS[1].items.filter((item) => (
  item.startsWith('Atrial fibrillation') || item.startsWith('Atrial flutter')
));

const HIGH_SCD = [
  'Sustained monomorphic VT', 'Polymorphic VT', 'Torsades de pointes',
  'Ventricular fibrillation / arrest survivor', 'Electrical storm history', 'Long QT syndrome',
  'Brugada syndrome', 'CPVT', 'ARVC', 'Hypertrophic cardiomyopathy',
];

const CHADSVASC = [
  { id: 'chf', label: 'Congestive heart failure / LV dysfunction', pts: 1 },
  { id: 'htn', label: 'Hypertension', pts: 1 },
  { id: 'age75', label: 'Age ≥ 75', pts: 2 },
  { id: 'dm', label: 'Diabetes mellitus', pts: 1 },
  { id: 'stroke', label: 'Prior stroke / TIA / thromboembolism', pts: 2 },
  { id: 'vasc', label: 'Vascular disease (MI, PAD, aortic plaque)', pts: 1 },
  { id: 'age65', label: 'Age 65–74', pts: 1 },
  { id: 'female', label: 'Sex category (female)', pts: 1 },
];

const HASBLED = [
  { id: 'h', label: 'Hypertension (uncontrolled, SBP > 160)', pts: 1 },
  { id: 'a', label: 'Abnormal renal or liver function', pts: 1 },
  { id: 's', label: 'Stroke history', pts: 1 },
  { id: 'b', label: 'Bleeding history or predisposition', pts: 1 },
  { id: 'l', label: 'Labile INR (on warfarin)', pts: 1 },
  { id: 'e', label: 'Elderly (age > 65)', pts: 1 },
  { id: 'd', label: 'Drugs (antiplatelet / NSAID) or alcohol', pts: 1 },
];

const COMORBIDITIES = [
  'Hypertension', 'Heart failure', 'Coronary artery disease / prior MI',
  'Valvular heart disease', 'Cardiomyopathy', 'Diabetes mellitus', 'Chronic kidney disease',
  'Prior stroke / TIA', 'Obstructive sleep apnea', 'COPD', 'Obesity', 'Thyroid disease',
];

const REVERSIBLE = [
  'Electrolyte abnormality (K⁺ / Mg²⁺)', 'Thyroid dysfunction', 'Anemia',
  'Active ischemia', 'Infection or fever', 'Alcohol use', 'Caffeine / energy drinks',
  'Stimulants or illicit drugs', 'QT-prolonging medications', 'OTC decongestants',
  'Untreated sleep apnea', 'Dehydration / deconditioning',
];

const MONITORING = [
  { id: 'hr', label: 'Heart rate & rhythm regularity', method: 'Home monitor / manual pulse' },
  { id: 'bp', label: 'Blood pressure', method: 'Home BP cuff (RPM)' },
  { id: 'wt', label: 'Weight / fluid status', method: 'Home scale (RPM)' },
  { id: 'spo2', label: 'Oxygen saturation', method: 'Pulse oximeter (RPM)' },
  { id: 'sx', label: 'Palpitations, dizziness, syncope', method: 'Symptom diary' },
  { id: 'ecg', label: 'Symptom-triggered ECG capture', method: 'Wearable / handheld ECG' },
  { id: 'amb', label: 'Ambulatory rhythm monitoring', method: 'Holter / patch / ILR' },
  { id: 'dev', label: 'Device interrogation', method: 'Remote transmission' },
  { id: 'inr', label: 'INR (if on warfarin)', method: 'Anticoag clinic / POC' },
  { id: 'lab', label: 'Electrolytes & renal function', method: 'Laboratory' },
  { id: 'qtc', label: 'QTc on antiarrhythmic therapy', method: '12-lead ECG' },
  { id: 'bleed', label: 'Signs of bleeding', method: 'Self-monitoring' },
];

const EDUCATION = [
  'What the arrhythmia is and why it matters', 'Stroke risk and anticoagulation adherence',
  'How to check pulse and use home devices', 'Warning signs and when to call 911',
  'Medication purpose, timing and side effects', 'Managing missed doses safely',
  'Bleeding precautions and what to report', 'Avoiding QT-prolonging and stimulant drugs',
  'Living with a pacemaker or ICD', 'What to do after an ICD shock',
  'Driving, exercise and travel guidance', 'Family screening (inherited syndromes)',
];

const LIFESTYLE = [
  'Limit alcohol', 'Limit caffeine', 'Tobacco cessation', 'Weight management',
  'Regular physical activity', 'Treat sleep apnea', 'Stress management', 'Adequate hydration',
];

const RESOURCES = [
  'Transportation assistance', 'Home health / RPM equipment',
  'Pharmacy support / adherence packaging', 'Financial or prescription-cost assistance',
  'Caregiver support / social work', 'Cardiac rehabilitation', 'Patient support group',
];

const INTERVENTIONS = [
  'Confirm PCP, cardiology and EP follow-up',
  'Order labs (electrolytes, renal, CBC, TSH, INR)', 'Arrange ambulatory rhythm monitoring / ECG',
  'Enroll or continue remote patient monitoring', 'Cardioversion', 'Catheter ablation',
  'Electrophysiology study', 'Device implant or generator change', 'Loop recorder placement',
  'Left atrial appendage occlusion', 'Sleep study', 'Cardiac rehabilitation referral',
  'Advance care planning discussion',
];

const OUTCOMES = [
  'Arrhythmia controlled with reduced symptom burden', 'Stroke and thromboembolism prevented',
  'Sudden cardiac death risk mitigated', 'Reversible contributors corrected',
  'Arrhythmia-related hospitalizations avoided', 'Patient participates in self-management',
];

const SCD_ITEMS = [
  'Unexplained or exertional syncope', 'Documented sustained VT / VF', 'LVEF ≤ 35%',
  'NYHA class II–III on optimal therapy', 'Family history of sudden cardiac death',
  'High-risk genotype identified', 'QTc > 500 ms', 'Spontaneous type 1 Brugada pattern',
];

const CONFIRMED_BY = ['ECG', 'Holter / event monitor', 'Loop recorder', 'Pacemaker (PPM)', 'ICD', 'EP study', 'Wearable'];
const RATE_CONTROL = ['Beta blocker', 'Non-dihydropyridine calcium channel blocker', 'Digoxin', 'AV node ablation + pacing', 'Not applicable'];
const RHYTHM_CONTROL = [
  'Class IB (mexiletine)', 'Class IC (flecainide / propafenone)',
  'Class III (amiodarone / sotalol / dofetilide)', 'Cardamyst (etripamil) nasal spray — as-needed PSVT conversion',
  'Pill-in-pocket strategy', 'Cardioversion planned', 'Catheter ablation referral', 'Not applicable',
];
const AA_SAFETY = [
  'QTc surveillance', 'Renal & hepatic function', 'Thyroid function (amiodarone)',
  'Pulmonary function / chest X-ray', 'Ophthalmology review', 'Initiation in monitored setting',
];
const DEVICE_TYPES = ['None', 'Pacemaker', 'ICD', 'CRT-P', 'CRT-D', 'Loop recorder (ILR)', 'Leadless pacemaker', 'S-ICD'];
const DEVICE_SAFETY = [
  'MRI-conditional status confirmed', 'Magnet response reviewed', 'Shock action plan given',
  'EMI precautions reviewed', 'End-of-life / deactivation discussed', 'Driving restrictions reviewed',
];
const NYHA_OPTS = ['I — no limitation', 'II — slight limitation', 'III — marked limitation', 'IV — symptoms at rest'];
const ANTICOAG = ['Not indicated — rationale documented', 'Apixaban', 'Rivaroxaban', 'Dabigatran', 'Edoxaban', 'Warfarin', 'LAA occlusion', 'Antiplatelet only'];

const scoreSum = (selected, rows) => (rows || []).reduce((sum, row) => (
  hasItem(selected, row.id) ? sum + row.pts : sum
), 0);

function createEmpty(patient) {
  const seed = patientSeed(patient);
  const arrhythmiaCond = findArrhythmiaCondition(patient);
  const primaryDiagnosis = arrhythmiaCond
    ? formatIcdOptionLabel({ code: arrhythmiaCond.icd_code, name: arrhythmiaCond.condition_name })
    : '';
  const diagnosisDate = arrhythmiaCond?.onset_date
    ? String(arrhythmiaCond.onset_date).slice(0, 10)
    : '';

  return {
    patient: {
      name: seed.name,
      firstName: seed.firstName || '',
      lastName: seed.lastName || '',
      dob: seed.dob,
      mrn: seed.mrn,
      sex: seed.sex,
      phone: seed.phone,
      language: '',
      emergencyContact: seed.emergencyContact || '',
      emergencyFirstName: seed.emergencyFirstName || '',
      emergencyLastName: seed.emergencyLastName || '',
      emergencyRelation: seed.emergencyRelation || '',
      emergencyPhone: seed.emergencyPhone || '',
      insurance: seed.insurance,
      insurancePrimary: seed.insurancePrimary,
      insuranceSecondary: seed.insuranceSecondary,
      startDate: '',
    },
    team: { careManager: '', caregiverIds: [], pcp: '', pcpUserId: '', cardiologist: '', ep: '', deviceClinic: '', anticoag: '' },
    diagnosis: { primary: primaryDiagnosis, date: diagnosisDate, confirmedBy: [], selected: [], notes: '' },
    baseline: { hr: '', rhythm: '', systolic: '', diastolic: '', pr: '', qrs: '', qtc: '', lvef: '', ecgDate: '', crcl: '', monitor: '' },
    comorbidities: [],
    reversible: [],
    risk: { chadsvasc: [], hasbled: [], scd: [], nyha: '', episodeFreq: '' },
    device: {
      type: 'None', manufacturer: '', model: '', serial: '', implantDate: '', indication: '',
      battery: '', lastCheck: '', remoteVendor: '', schedule: '', shocks: '', safety: [],
    },
    goals: [
      { id: 'g1', goal: 'Resting heart rate in target range', target: '60–100 bpm', date: '' },
      { id: 'g2', goal: 'Blood pressure at goal', target: '< 130/80 mmHg', date: '' },
      { id: 'g3', goal: 'Symptom burden reduced', target: 'NYHA class ≤ II', date: '' },
    ],
    meds: {
      anticoag: '', inrGoal: '', rate: [], rhythm: [], safety: [], list: [],
      reconciled: false, renalReviewed: false, interactionsChecked: false,
    },
    monitoring: {},
    emergency: { lowHr: '', highHr: '', actions: '', cprTrained: '' },
    interventions: [],
    outcomes: [],
    education: { topics: [], lifestyle: [], teachBack: false, materials: false, caregiver: false },
    resources: { needs: '', arranged: [] },
    signoff: {
      patientSigned: false, patientDate: '', managerName: '', managerDate: '',
      providerName: '', providerDate: '', reviewInterval: 'Annually', nextReview: '',
    },
  };
}

function derive(plan) {
  const selected = plan?.diagnosis?.selected || [];
  const hasAF = selected.some((item) => AF_FLUTTER.includes(item));
  const hasSCD = selected.some((item) => HIGH_SCD.includes(item));
  const hasDevice = Boolean(plan?.device?.type && plan.device.type !== 'None');
  const chads = scoreSum(plan?.risk?.chadsvasc, CHADSVASC);
  const bled = scoreSum(plan?.risk?.hasbled, HASBLED);
  const flags = [];
  if (hasAF && chads >= 2 && !plan?.meds?.anticoag) {
    flags.push({ level: 'high', text: 'AF with CHA₂DS₂-VASc ≥ 2 and no anticoagulation strategy recorded.' });
  }
  if (hasAF && !(plan?.risk?.chadsvasc || []).length) {
    flags.push({ level: 'med', text: 'AF or flutter selected — complete CHA₂DS₂-VASc scoring.' });
  }
  if (bled >= 3) {
    flags.push({ level: 'med', text: 'HAS-BLED ≥ 3 — address modifiable bleeding risks and shorten review interval.' });
  }
  const qtc = parseInt(plan?.baseline?.qtc, 10);
  if (!Number.isNaN(qtc) && qtc >= 500) {
    flags.push({ level: 'high', text: 'QTc ≥ 500 ms — review QT-prolonging agents before rhythm-control therapy.' });
  }
  if (hasItem(plan?.meds?.rhythm, 'Class III (amiodarone / sotalol / dofetilide)') && !hasItem(plan?.meds?.safety, 'QTc surveillance')) {
    flags.push({ level: 'med', text: 'Class III agent selected — add QTc surveillance to the monitoring plan.' });
  }
  if (hasSCD && plan?.device?.type === 'None') {
    flags.push({ level: 'med', text: 'High sudden-death-risk diagnosis with no device — document ICD evaluation.' });
  }
  if (hasDevice && !plan?.device?.lastCheck) {
    flags.push({ level: 'low', text: 'Device recorded without a last interrogation date.' });
  }
  if (plan?.meds?.anticoag === 'Warfarin' && !plan?.meds?.inrGoal) {
    flags.push({ level: 'low', text: 'Warfarin selected — record the target INR range.' });
  }
  if ((plan?.meds?.list || []).length && !plan?.meds?.reconciled) {
    flags.push({ level: 'low', text: 'Medication reconciliation not yet marked complete.' });
  }
  const completion = countDone({
    patient: Boolean(plan?.patient?.name && plan?.patient?.dob && plan?.team?.careManager),
    diagnosis: Boolean(plan?.diagnosis?.primary && selected.length),
    baseline: Boolean(plan?.baseline?.hr && plan?.baseline?.rhythm),
    risk: hasAF ? ((plan?.risk?.chadsvasc || []).length > 0 || (plan?.risk?.hasbled || []).length > 0) : Boolean(plan?.risk?.nyha),
    device: hasDevice ? Boolean(plan?.device?.implantDate) : false,
    goals: (plan?.goals || []).some((goal) => String(goal.date || '').trim()),
    meds: (plan?.meds?.list || []).length > 0 || Boolean(plan?.meds?.anticoag),
    monitoring: Object.keys(plan?.monitoring || {}).length > 0,
    emergency: Boolean(plan?.emergency?.actions),
    education: (plan?.education?.topics || []).length > 0,
    signoff: Boolean(plan?.signoff?.managerName && plan?.signoff?.nextReview),
  }, 11);
  const scoreCards = hasAF ? [
    { label: 'CHA₂DS₂-VASc', value: chads, note: chads >= 2 ? 'Anticoagulation generally indicated' : chads === 1 ? 'Consider anticoagulation' : 'Low risk' },
    { label: 'HAS-BLED', value: bled, note: bled >= 3 ? 'High — correct modifiable risks' : 'Not high' },
  ] : [];
  return {
    hasAF,
    hasDevice,
    flags,
    completion,
    scoreCards,
    tags: selected,
    tagTitle: 'Diagnoses selected',
  };
}

function sections(program) {
  return [
    {
      id: 'patient', n: '01', label: 'Patient & care team',
      cards: [
        card('Patient information', patientInformationChildren(program)),
        ...(program === 'PCM' ? [insuranceCard()] : []),
        card('Care team', [
          grid(2, careTeamFields(program, program === 'PCM' ? [
            field('Device clinic / remote monitoring', 'team.deviceClinic', 'text'),
            field('Anticoagulation clinic / pharmacist', 'team.anticoag', 'text'),
          ] : [
            field('Cardiologist', 'team.cardiologist', 'text'),
            field('Electrophysiologist', 'team.ep', 'text'),
            field('Device clinic / remote monitoring', 'team.deviceClinic', 'text'),
            field('Anticoagulation clinic / pharmacist', 'team.anticoag', 'text'),
          ])),
        ], { subtitle: 'Everyone with a named responsibility in this plan.' }),
      ],
    },
    {
      id: 'diagnosis', n: '02', label: 'Diagnosis & classification',
      cards: [
        card('Primary diagnosis', [
          grid(2, [
            field('Primary arrhythmia diagnosis', 'diagnosis.primary', 'select', {
              required: true,
              disabled: true,
              optionSource: 'cmsIcd',
              searchTerms: ['arrhythmia', 'atrial fibrillation', 'atrial flutter', 'bradycardia', 'tachycardia', 'heart block'],
              placeholder: 'Search or type a diagnosis',
            }),
            field('Date of diagnosis', 'diagnosis.date', 'date', { disabled: true }),
          ]),
          chips('Confirmed by', 'diagnosis.confirmedBy', CONFIRMED_BY, { nowrap: true }),
        ]),
        card('Additional arrhythmias', [
          {
            type: 'accordion',
            path: 'diagnosis.selected',
            groups: ARRHYTHMIA_GROUPS,
          },
        ], { subtitle: 'Open a category and tick everything that applies.' }),
        card('Contributing factors', [
          {
            type: 'accordion',
            groups: [
              { id: 'comorbid', label: 'Comorbid conditions', path: 'comorbidities', items: COMORBIDITIES },
              { id: 'reversible', label: 'Reversible or aggravating factors', path: 'reversible', items: REVERSIBLE, tone: 'amber' },
            ],
          },
        ], { subtitle: 'Correcting these often does more than escalating drug therapy.' }),
      ],
    },
    {
      id: 'baseline', n: '03', label: 'Baseline data',
      cards: [
        card('Baseline diagnostic data', [
          grid(3, [
            field('Heart rate (bpm)', 'baseline.hr', 'number', { required: true, placeholder: 'e.g. 72' }),
            field('Systolic', 'baseline.systolic', 'number', { placeholder: 'e.g. 128' }),
            field('Diastolic', 'baseline.diastolic', 'number', { placeholder: 'e.g. 78' }),
            field('Rhythm', 'baseline.rhythm', 'select', { required: true, options: ['Sinus', 'Atrial fibrillation', 'Atrial flutter', 'Paced', 'Junctional', 'Other'] }),
            field('PR interval (ms)', 'baseline.pr', 'number', { placeholder: 'e.g. 160' }),
            field('QRS duration (ms)', 'baseline.qrs', 'number', { placeholder: 'e.g. 94' }),
            field('QTc (ms)', 'baseline.qtc', 'number', { placeholder: 'e.g. 430', hint: 'Flagged automatically at 500 ms or above.' }),
            field('LVEF (%)', 'baseline.lvef', 'number', { placeholder: 'e.g. 55' }),
            field('Creatinine clearance', 'baseline.crcl', 'number', { placeholder: 'e.g. 60', hint: 'mL/min' }),
            field('Last ECG date', 'baseline.ecgDate', 'date'),
          ]),
          field('Ambulatory monitor type & duration', 'baseline.monitor', 'text', { placeholder: 'e.g. 14-day patch monitor, PVC burden 8%' }),
        ], { subtitle: 'Values at the time this plan was written.' }),
      ],
    },
    {
      id: 'risk', n: '04', label: 'Risk stratification',
      cards: [
        card('Thromboembolic risk', [
          { type: 'score', path: 'risk.chadsvasc', items: CHADSVASC, totalLabel: 'Total CHA₂DS₂-VASc' },
        ], { subtitle: 'CHA₂DS₂-VASc — shown because atrial fibrillation or flutter is selected.', showWhen: (_plan, ctx) => ctx.hasAF }),
        card('Bleeding risk', [
          { type: 'score', path: 'risk.hasbled', items: HASBLED, totalLabel: 'Total HAS-BLED' },
        ], { subtitle: 'HAS-BLED — use to correct modifiable risks, not to withhold anticoagulation.', showWhen: (_plan, ctx) => ctx.hasAF }),
        card('Sudden cardiac death & syncope risk', [
          chips('Sudden cardiac death risk', 'risk.scd', SCD_ITEMS, { tone: 'red' }),
        ]),
        card('Symptom burden', [
          grid(2, [
            field('NYHA functional class', 'risk.nyha', 'select', { options: NYHA_OPTS }),
            field('Episode frequency', 'risk.episodeFreq', 'select', { options: ['Daily', 'Weekly', 'Monthly', 'Rare', 'Continuous'] }),
          ]),
        ]),
      ],
    },
    {
      id: 'device', n: '05', label: 'Device management',
      cards: [
        card('Implanted device', [
          { type: 'pills', path: 'device.type', items: DEVICE_TYPES },
        ]),
        card('Device details', [
          grid(2, [
            field('Manufacturer', 'device.manufacturer', 'text'),
            field('Model', 'device.model', 'text'),
            field('Serial number', 'device.serial', 'text'),
            field('Implant date', 'device.implantDate', 'date', { required: true }),
            field('Indication', 'device.indication', 'text'),
            field('Battery status / ERI date', 'device.battery', 'text'),
            field('Last interrogation', 'device.lastCheck', 'date'),
            field('Remote monitoring vendor', 'device.remoteVendor', 'text'),
            field('Transmission schedule', 'device.schedule', 'select', { options: ['Every 30 days', 'Every 60 days', 'Every 90 days', 'Every 6 months', 'Annually'] }),
            field('Shocks / ATP since last review', 'device.shocks', 'text', { placeholder: 'e.g. 0' }),
          ]),
        ], { showWhen: (_plan, ctx) => ctx.hasDevice }),
        card('Device safety counseling', [
          chips('Device safety', 'device.safety', DEVICE_SAFETY),
        ], { showWhen: (_plan, ctx) => ctx.hasDevice }),
        card(null, [
          { type: 'empty', title: 'No implanted device recorded.', text: 'Select a device type above to record interrogation schedules, remote monitoring and safety counseling.' },
        ], { showWhen: (_plan, ctx) => !ctx.hasDevice }),
      ],
    },
    {
      id: 'goals', n: '06', label: 'Goals & outcomes',
      cards: goalsCard(OUTCOMES, INTERVENTIONS),
    },
    {
      id: 'meds', n: '07', label: 'Medications',
      cards: [
        card('Anticoagulation strategy', [
          grid(2, [
            field('Strategy', 'meds.anticoag', 'select', { options: ANTICOAG, requiredWhen: (_plan, ctx) => ctx.hasAF }),
            field('Target INR range', 'meds.inrGoal', 'text', {
              required: true,
              placeholder: 'e.g. 2.0 – 3.0',
              showWhen: (plan) => plan?.meds?.anticoag === 'Warfarin',
            }),
          ]),
        ]),
        card('Rate & rhythm control', [
          {
            type: 'accordion',
            groups: [
              { id: 'rate', label: 'Rate control', path: 'meds.rate', items: RATE_CONTROL },
              { id: 'rhythm', label: 'Rhythm control', path: 'meds.rhythm', items: RHYTHM_CONTROL },
              { id: 'safety', label: 'Antiarrhythmic safety monitoring', path: 'meds.safety', items: AA_SAFETY },
            ],
          },
        ]),
        medicationListCard(),
      ],
    },
    {
      id: 'monitoring', n: '08', label: 'Monitoring plan',
      cards: [monitorCard(MONITORING)],
    },
    {
      id: 'emergency', n: '09', label: 'Emergency plan',
      cards: [
        ...emergencyCards('Fainting, chest pain, severe shortness of breath, one-sided weakness, facial droop, trouble speaking, uncontrolled bleeding, or repeated device shocks.').map((block, index) => (
          index === 1
            ? card('Patient-specific thresholds', [
              grid(2, [
                field('Call the care team if resting pulse is below', 'emergency.lowHr', 'number', { placeholder: 'e.g. 50' }),
                field('Call the care team if resting pulse is above', 'emergency.highHr', 'number', { placeholder: 'e.g. 120' }),
              ]),
              field('Patient-specific action steps', 'emergency.actions', 'textarea', {
                required: true,
                rows: 4,
                placeholder: 'What this patient should do, in order, when symptoms start — including who to call and when.',
              }),
              field('Household trained in CPR / AED access', 'emergency.cprTrained', 'select', { options: ['Yes', 'No', 'Referral made'] }),
            ])
            : block
        )),
      ],
    },
    {
      id: 'education', n: '10', label: 'Education & resources',
      cards: educationCards(EDUCATION, LIFESTYLE, RESOURCES),
    },
    {
      id: 'signoff', n: '11', label: 'Review & sign-off',
      cards: signoffCards(program),
    },
  ];
}

const arrhythmiaDisease = {
  id: 'arrhythmia',
  label: 'Arrhythmia',
  title: 'Arrhythmia care plan',
  summary: 'Rhythm, stroke risk, devices, and antiarrhythmic safety.',
  frequencies: FREQUENCIES,
  createEmpty,
  derive,
  sections,
};

export default arrhythmiaDisease;
